import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';

import { type PartnerAcquisitionLink } from '@/api/routes/partners/partners.acquisition.api';
import Chip from '@/components/Chip';
import { IconCopy } from '@/components/icon/tabler/IconCopy';
import {
  acquisitionLinkStatusLabel,
  formatAcquisitionBenefit,
  formatAcquisitionCount,
  formatLinkCampaign,
  getAcquisitionIndicators,
} from '@/domain/acquisition/utils';
import OfferCouponCode from '@/domain/offers/components/OfferCouponCode';
import { useTranslate } from '@/state/i18n';
import { copyText } from '@/utils/clipboard';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  link: PartnerAcquisitionLink;
}

/** One tracked link: the short URL to share, its coupon and what it brought in the window. */
const AcquisitionLinkCard = ({ link }: Props) => {
  const t = useTranslate('domain.acquisition.components.AcquisitionLinkCard');
  const { enqueueSnackbar } = useSnackbar();
  const indicators = getAcquisitionIndicators(link.numbers);
  const benefit = formatAcquisitionBenefit(link.benefit);

  const handleCopy = async (text: string, success: 'linkCopied' | 'couponCopied') => {
    const copied = await copyText(text);
    enqueueSnackbar(t(copied ? success : 'copyError'), {
      variant: copied ? 'success' : 'error',
    });
  };

  const numbers = [
    { label: t('clicks'), value: indicators.clicks },
    { label: t('signups'), value: indicators.signups },
    { label: t('paying'), value: indicators.paying },
  ];

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Stack gap={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
            {link.label}
          </Typography>
          {link.campaign ? (
            <Typography variant="caption" color="text.secondary">
              {formatLinkCampaign(link.campaign)}
            </Typography>
          ) : null}
        </Stack>
        <Chip
          size="xsmall"
          label={acquisitionLinkStatusLabel[link.status]}
          color={link.status === 'active' ? 'success' : 'neutral'}
          variant={link.status === 'active' ? 'solid' : 'outline'}
        />
      </Stack>

      <Stack direction="row" alignItems="center" gap={1} sx={sx.urlRow}>
        <Typography variant="body2" sx={sx.url}>
          {link.shortUrl}
        </Typography>
        <Tooltip title={t('copyLink')}>
          <IconButton
            size="small"
            aria-label={t('copyLink')}
            onClick={() => handleCopy(link.shortUrl, 'linkCopied')}
          >
            <IconCopy fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {link.couponCode ? (
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <OfferCouponCode code={link.couponCode} />
          <Tooltip title={t('copyCoupon')}>
            <IconButton
              size="small"
              aria-label={t('copyCoupon')}
              onClick={() => handleCopy(link.couponCode as string, 'couponCopied')}
            >
              <IconCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          {benefit ? (
            <Typography variant="caption" color="text.secondary">
              {benefit}
            </Typography>
          ) : null}
        </Stack>
      ) : null}

      <Box sx={sx.numbers}>
        {numbers.map((item) => (
          <Stack key={item.label} gap={0.25}>
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="subtitle1" component="p" fontWeight={700}>
              {formatAcquisitionCount(item.value)}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Paper>
  );
};

export default AcquisitionLinkCard;

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  urlRow: {
    px: 1.5,
    py: 0.5,
    borderRadius: radiusTokens.xl,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.default',
  },
  url: { flex: 1, minWidth: 0, fontFamily: 'monospace', overflowWrap: 'anywhere' },
  numbers: {
    display: 'grid',
    gap: 1,
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    pt: 1.5,
    borderTop: '1px solid',
    borderColor: 'divider',
  },
};
